import React, { useEffect, useRef, useState } from 'react';
import { animalApi, inventoryApi, feedingApi, boxApi, farmApi, masterDataApi, API_BASE_URL } from '../services/api';
import { handleApiError, showError, showSuccess } from '../utils/handleApiError';
import { Animal, Box, Farm, Stable } from '../types';
import { useLanguage } from '../contexts/LanguageContext';
import ClearableSelect from '../components/ClearableSelect';
import Pagination from '../components/Pagination';

interface FeedInventoryItem {
  id: number;
  feed?: {
    id: number;
    name: string;
  };
  quantity: number;
  quantity_unit: string;
  display_name?: string;
}

interface FeedingData {
  farm_id: number;
  feed_inventory_id: number;
  feeding_type: 'individual' | 'box';
  quantity: number;
  fed_at: string;
  notes: string;
  animal_id?: number;
  box_id?: number;
}

const Feeding: React.FC = () => {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState<'individual' | 'group' | 'stable'>('individual');
  const [selectedAnimals, setSelectedAnimals] = useState<number[]>([]);
  const [selectedBoxes, setSelectedBoxes] = useState<number[]>([]);
  const [selectedStable, setSelectedStable] = useState<number | null>(null);
  const [animals, setAnimals] = useState<Animal[]>([]);
  const [boxes, setBoxes] = useState<Box[]>([]);
  const [stables, setStables] = useState<any[]>([]);
  const [availableFeeds, setAvailableFeeds] = useState<FeedInventoryItem[]>([]);
  const [feedQuantityUnits, setFeedQuantityUnits] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [animalsLoading, setAnimalsLoading] = useState(false);
  const [distributionMethod, setDistributionMethod] = useState<'equal_per_animal' | 'equal_per_box'>('equal_per_animal');

  // Farm state
  const [farms, setFarms] = useState<Farm[]>([]);
  const [selectedFarmId, setSelectedFarmId] = useState<number | null>(null);

  // Hierarchical filter states
  const [stableFilter, setStableFilter] = useState<number | null>(null);
  const [boxFilter, setBoxFilter] = useState<number | null>(null);

  // Search and filter states
  const [animalSearchTerm, setAnimalSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [animalBreedFilter, setAnimalBreedFilter] = useState('');
  const [boxSearchTerm, setBoxSearchTerm] = useState('');
  const [boxCapacityFilter, setBoxCapacityFilter] = useState('');

  // Pagination state (individual tab)
  const [animalPage, setAnimalPage] = useState(1);
  const [animalLastPage, setAnimalLastPage] = useState(1);
  const [animalPerPage, setAnimalPerPage] = useState(50);
  const [animalTotal, setAnimalTotal] = useState(0);

  // Selection summary
  const [selectionExpanded, setSelectionExpanded] = useState(false);
  const [selectedAnimalMap, setSelectedAnimalMap] = useState<Map<number, string>>(new Map());
  
  const [feedingForm, setFeedingForm] = useState({
    feed_inventory_id: '',
    quantity: '',
    quantity_unit: '',
    fed_at: new Date().toISOString().split('T')[0],
    notes: ''
  });

  // Load farms and feed units on mount
  useEffect(() => {
    const init = async () => {
      try {
        const [farmsData, feedUnitsCategory] = await Promise.all([
          farmApi.getAll(),
          masterDataApi.getCategory('quantity_units')
        ]);
        setFarms(farmsData);
        setFeedQuantityUnits(feedUnitsCategory.values || []);
        if (farmsData.length > 0) {
          setSelectedFarmId(farmsData[0].id);
        }
      } catch (error) {
        console.error('Error loading initial data:', error);
        handleApiError(error, 'Hiba az alapadatok betöltésekor');
      }
    };
    init();
  }, []);

  // Load farm-dependent data when farm changes
  useEffect(() => {
    if (selectedFarmId) {
      loadFarmData();
    }
  }, [selectedFarmId]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(animalSearchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [animalSearchTerm]);

  // Load animals when filters, page, or search change
  const prevFilterDeps = useRef({ debouncedSearchTerm, animalBreedFilter, stableFilter, boxFilter, selectedFarmId });
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (!selectedFarmId) return;

    const filtersChanged = !isInitialMount.current && (
      prevFilterDeps.current.debouncedSearchTerm !== debouncedSearchTerm ||
      prevFilterDeps.current.animalBreedFilter !== animalBreedFilter ||
      prevFilterDeps.current.stableFilter !== stableFilter ||
      prevFilterDeps.current.boxFilter !== boxFilter ||
      prevFilterDeps.current.selectedFarmId !== selectedFarmId
    );
    prevFilterDeps.current = { debouncedSearchTerm, animalBreedFilter, stableFilter, boxFilter, selectedFarmId };
    isInitialMount.current = false;

    if (filtersChanged && animalPage !== 1) {
      setAnimalPage(1);
      return;
    }

    loadAnimals(animalPage);
  }, [animalPage, debouncedSearchTerm, animalBreedFilter, stableFilter, boxFilter, selectedFarmId]);

  const handleFarmChange = (farmId: number) => {
    setSelectedFarmId(farmId);
    // Reset hierarchical filters and selections
    setStableFilter(null);
    setBoxFilter(null);
    setAnimalSearchTerm('');
    setAnimalBreedFilter('');
    setAnimalPage(1);
    setSelectedAnimals([]);
    setSelectedAnimalMap(new Map());
    setSelectionExpanded(false);
    setSelectedBoxes([]);
    setSelectedStable(null);
  };

  const handleStableFilterChange = (stableId: number | null) => {
    setStableFilter(stableId);
    setBoxFilter(null); // Reset box filter when stable changes
    setSelectedBoxes([]);
  };

  const handleBoxFilterChange = (boxId: number | null) => {
    setBoxFilter(boxId);
  };

  const loadFarmData = async () => {
    if (!selectedFarmId) return;
    try {
      setLoading(true);
      const [boxesResponse, feedsResponse, stablesResponse] = await Promise.all([
        boxApi.getAll(selectedFarmId),
        inventoryApi.getFeeds(selectedFarmId, true),
        fetch(`${API_BASE_URL}/farms/${selectedFarmId}/stables`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        })
      ]);

      setBoxes(boxesResponse || []);
      setAvailableFeeds(feedsResponse || []);

      if (stablesResponse.ok) {
        const stablesData = await stablesResponse.json();
        setStables(stablesData || []);
      }
    } catch (error) {
      console.error('Error loading farm data:', error);
      handleApiError(error, 'Hiba a telep adatainak betöltésekor');
    } finally {
      setLoading(false);
    }
  };

  const loadAnimals = async (page: number = 1) => {
    if (!selectedFarmId) return;
    try {
      setAnimalsLoading(true);
      const params: any = { status: 'aktiv', farm_id: selectedFarmId, page };
      if (debouncedSearchTerm) params.search = debouncedSearchTerm;
      if (animalBreedFilter) params.breed = animalBreedFilter;
      if (stableFilter) params.stable_id = stableFilter;
      if (boxFilter) params.box_id = boxFilter;
      const response = await animalApi.getAll(params);
      setAnimals(response.data || []);
      setAnimalLastPage(response.last_page);
      setAnimalPerPage(response.per_page);
      setAnimalTotal(response.total);
    } catch (error) {
      console.error('Error loading animals:', error);
      handleApiError(error, 'Hiba az állatok betöltésekor');
    } finally {
      setAnimalsLoading(false);
    }
  };

  const handleAnimalSelection = (animalId: number) => {
    setSelectedAnimals(prev => {
      if (prev.includes(animalId)) {
        setSelectedAnimalMap(m => { const next = new Map(m); next.delete(animalId); return next; });
        return prev.filter(id => id !== animalId);
      } else {
        const animal = animals.find(a => a.id === animalId);
        if (animal) {
          setSelectedAnimalMap(m => new Map(m).set(animalId, animal.ear_tag));
        }
        return [...prev, animalId];
      }
    });
  };

  const handleBoxSelection = (boxId: number) => {
    setSelectedBoxes(prev => 
      prev.includes(boxId) 
        ? prev.filter(id => id !== boxId)
        : [...prev, boxId]
    );
  };

  // Derive stable/box options from farms data for hierarchical filters
  const selectedFarm = farms.find(f => f.id === selectedFarmId);
  const stableOptions: Stable[] = selectedFarm?.stables || stables.map(s => ({ id: s.id, farm_id: selectedFarmId!, name: s.name, capacity: s.capacity }));
  const boxOptions: Box[] = stableFilter
    ? boxes.filter(b => b.stable_id === stableFilter)
    : boxes;

  const filteredBoxes = boxes.filter(box => {
    const matchesSearch = box.name.toLowerCase().includes(boxSearchTerm.toLowerCase());
    const matchesStable = !stableFilter || box.stable_id === stableFilter;
    const matchesCapacity = !boxCapacityFilter ||
      (boxCapacityFilter === 'small' && box.capacity <= 10) ||
      (boxCapacityFilter === 'medium' && box.capacity > 10 && box.capacity <= 30) ||
      (boxCapacityFilter === 'large' && box.capacity > 30);

    return matchesSearch && matchesStable && matchesCapacity;
  });

  // Get unique values for filter dropdowns
  const uniqueBreeds = Array.from(new Set(animals.map(animal => animal.breed))).sort();
  const pageAnimalIds = animals.map(a => a.id);
  const allPageSelected = animals.length > 0 && animals.every(a => selectedAnimals.includes(a.id));

  const handleSelectAllAnimals = () => {
    if (allPageSelected) {
      // Deselect all on current page
      setSelectedAnimals(prev => prev.filter(id => !pageAnimalIds.includes(id)));
      setSelectedAnimalMap(m => {
        const next = new Map(m);
        pageAnimalIds.forEach(id => next.delete(id));
        return next;
      });
    } else {
      // Add all on current page to selection
      const newSelected = Array.from(new Set([...selectedAnimals, ...pageAnimalIds]));
      setSelectedAnimals(newSelected);
      setSelectedAnimalMap(m => {
        const next = new Map(m);
        animals.forEach(a => next.set(a.id, a.ear_tag));
        return next;
      });
    }
  };

  const handleSelectAllBoxes = () => {
    if (selectedBoxes.length === filteredBoxes.length && filteredBoxes.every(box => selectedBoxes.includes(box.id))) {
      setSelectedBoxes(prev => prev.filter(id => !filteredBoxes.map(b => b.id).includes(id)));
    } else {
      const newSelected = Array.from(new Set([...selectedBoxes, ...filteredBoxes.map(box => box.id)]));
      setSelectedBoxes(newSelected);
    }
  };

  const handleSubmit = async () => {
    try {
      if (!feedingForm.feed_inventory_id || !feedingForm.quantity) {
        showError(t('fill_required_fields', 'feedings'));
        return;
      }

      // Check if enough feed is in stock
      const selectedFeed = availableFeeds.find(feed =>
        feed.id.toString() === feedingForm.feed_inventory_id
      );

      // Handle stable-level feeding separately
      if (activeTab === 'stable') {
        if (!selectedStable) {
          showError(t('select_stable', 'feedings'));
          return;
        }

        const totalQuantityNeeded = parseFloat(feedingForm.quantity);

        if (selectedFeed && totalQuantityNeeded > selectedFeed.quantity) {
          showError(`${t('insufficient_feed_stock', 'feedings')} ${selectedFeed.quantity} ${selectedFeed.quantity_unit}, ${t('required', 'feedings')} ${totalQuantityNeeded} ${feedingForm.quantity_unit}`);
          return;
        }

        const response = await fetch(`${API_BASE_URL}/feedings/stable-bulk`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
          body: JSON.stringify({
            farm_id: selectedFarmId,
            stable_id: selectedStable,
            feed_inventory_id: parseInt(feedingForm.feed_inventory_id),
            total_quantity: totalQuantityNeeded,
            fed_at: feedingForm.fed_at,
            distribution_method: distributionMethod,
            notes: feedingForm.notes
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to create stable feeding');
        }

        const result = await response.json();
        showSuccess(`${result.message} — Istálló: ${result.stable}, Össz mennyiség: ${result.total_quantity} ${feedingForm.quantity_unit}, Boxok száma: ${result.boxes_fed}`);

        // Reset form and selections
        setFeedingForm({
          feed_inventory_id: '',
          quantity: '',
          quantity_unit: '',
          fed_at: new Date().toISOString().split('T')[0],
          notes: ''
        });
        setSelectedStable(null);

        // Reload data to update inventory
        loadFarmData();
        return;
      }

      const totalQuantityNeeded = activeTab === 'individual'
        ? parseFloat(feedingForm.quantity) * selectedAnimals.length
        : parseFloat(feedingForm.quantity) * selectedBoxes.length;

      if (selectedFeed && totalQuantityNeeded > selectedFeed.quantity) {
        showError(`${t('insufficient_feed_stock', 'feedings')} ${selectedFeed.quantity} ${selectedFeed.quantity_unit}, ${t('required', 'feedings')} ${totalQuantityNeeded} ${feedingForm.quantity_unit}`);
        return;
      }

      const feedingsToCreate: FeedingData[] = [];

      if (activeTab === 'individual') {
        if (selectedAnimals.length === 0) {
          showError(t('select_at_least_one_animal', 'feedings'));
          return;
        }

        selectedAnimals.forEach(animalId => {
          feedingsToCreate.push({
            farm_id: selectedFarmId!,
            feed_inventory_id: parseInt(feedingForm.feed_inventory_id),
            animal_id: animalId,
            feeding_type: 'individual',
            quantity: parseFloat(feedingForm.quantity),
            fed_at: feedingForm.fed_at,
            notes: feedingForm.notes
          });
        });
      } else {
        if (selectedBoxes.length === 0) {
          showError(t('select_at_least_one_box', 'feedings'));
          return;
        }

        selectedBoxes.forEach(boxId => {
          feedingsToCreate.push({
            farm_id: selectedFarmId!,
            feed_inventory_id: parseInt(feedingForm.feed_inventory_id),
            box_id: boxId,
            feeding_type: 'box',
            quantity: parseFloat(feedingForm.quantity),
            fed_at: feedingForm.fed_at,
            notes: feedingForm.notes
          });
        });
      }

      // Create all feedings
      await Promise.all(feedingsToCreate.map(feeding => feedingApi.create(feeding)));

      showSuccess(`${feedingsToCreate.length} ${t('feedings_recorded_successfully', 'feedings')}`);
      
      // Reset form and selections
      setFeedingForm({
        feed_inventory_id: '',
        quantity: '',
        quantity_unit: '',
        fed_at: new Date().toISOString().split('T')[0],
        notes: ''
      });
      setSelectedAnimals([]);
      setSelectedAnimalMap(new Map());
      setSelectionExpanded(false);
      setSelectedBoxes([]);

      // Reload data to update inventory
      loadFarmData();
    } catch (error: any) {
      console.error('Error creating feedings:', error);
      handleApiError(error, 'Hiba az etetés rögzítésekor');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">{t('title', 'feedings')}</h1>
        <div className="flex items-center gap-2">
          <label htmlFor="feeding-farm-select" className="text-sm font-medium text-gray-700">Telep:</label>
          <select
            id="feeding-farm-select"
            value={selectedFarmId || ''}
            onChange={(e) => handleFarmChange(parseInt(e.target.value))}
            className="rounded-md border-gray-300 shadow-sm focus-visible:border-green-500 focus-visible:ring-green-500 text-sm py-1.5 pl-3 pr-8"
          >
            {farms.map((farm) => (
              <option key={farm.id} value={farm.id}>{farm.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('individual')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'individual'
                ? 'border-green-500 text-green-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            {t('individual_feeding', 'feedings')}
          </button>
          <button
            onClick={() => setActiveTab('group')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'group'
                ? 'border-green-500 text-green-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            {t('group_feeding', 'feedings')}
          </button>
          <button
            onClick={() => setActiveTab('stable')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'stable'
                ? 'border-green-500 text-green-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            {t('stable_feeding', 'feedings')}
          </button>
        </nav>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Selection Panel */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {activeTab === 'individual' ? t('select_animals', 'feedings') :
                 activeTab === 'group' ? t('select_boxes', 'feedings') :
                 t('select_stable', 'feedings')}
              </h3>
              {activeTab !== 'stable' && (
                <button
                  onClick={activeTab === 'individual' ? handleSelectAllAnimals : handleSelectAllBoxes}
                  className="text-sm text-green-600 hover:text-green-700"
                >
                  {(activeTab === 'individual'
                    ? allPageSelected
                    : (selectedBoxes.length === filteredBoxes.length && filteredBoxes.every(box => selectedBoxes.includes(box.id))))
                    ? t('deselect_all', 'feedings') : t('select_all', 'feedings')}
                </button>
              )}
            </div>

            {/* Search and Filter Controls */}
            {activeTab === 'stable' ? (
              <div className="mb-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('stable', 'feedings')} *</label>
                  <select
                    value={selectedStable || ''}
                    onChange={(e) => setSelectedStable(parseInt(e.target.value) || null)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    <option value="">{t('select_stable', 'feedings')}</option>
                    {stables.map((stable) => (
                      <option key={stable.id} value={stable.id}>
                        {stable.name} ({stable.current_animal_count || 0} {t('animals', 'feedings')})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('distribution_method', 'feedings')} *</label>
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <input
                        type="radio"
                        id="equal_per_animal"
                        value="equal_per_animal"
                        checked={distributionMethod === 'equal_per_animal'}
                        onChange={(e) => setDistributionMethod('equal_per_animal')}
                        className="h-4 w-4 text-green-600 focus:ring-green-500"
                      />
                      <label htmlFor="equal_per_animal" className="ml-2 text-sm text-gray-700">
                        {t('equal_per_animal', 'feedings')} <span className="text-gray-500">({t('equal_per_animal_desc', 'feedings')})</span>
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="radio"
                        id="equal_per_box"
                        value="equal_per_box"
                        checked={distributionMethod === 'equal_per_box'}
                        onChange={(e) => setDistributionMethod('equal_per_box')}
                        className="h-4 w-4 text-green-600 focus:ring-green-500"
                      />
                      <label htmlFor="equal_per_box" className="ml-2 text-sm text-gray-700">
                        {t('equal_per_box', 'feedings')} <span className="text-gray-500">({t('equal_per_box_desc', 'feedings')})</span>
                      </label>
                    </div>
                  </div>
                </div>

                {selectedStable && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-blue-900 mb-2">{t('distribution_info', 'feedings')}</h4>
                    <p className="text-sm text-blue-700">
                      {t('stable_distribution_info', 'feedings')}
                    </p>
                  </div>
                )}
              </div>
            ) : activeTab === 'individual' ? (
              <div className="mb-4 space-y-3">
                {/* Hierarchical filters: Stable → Box */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Istálló</label>
                    <ClearableSelect
                      value={String(stableFilter || '')}
                      onChange={(val) => handleStableFilterChange(val ? parseInt(val) : null)}
                      onClear={() => handleStableFilterChange(null)}
                      placeholder="Összes istálló"
                    >
                      {stableOptions.map(stable => (
                        <option key={stable.id} value={stable.id}>{stable.name}</option>
                      ))}
                    </ClearableSelect>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Box</label>
                    <ClearableSelect
                      value={String(boxFilter || '')}
                      onChange={(val) => handleBoxFilterChange(val ? parseInt(val) : null)}
                      onClear={() => handleBoxFilterChange(null)}
                      placeholder="Összes box"
                    >
                      {boxOptions.map(box => (
                        <option key={box.id} value={box.id}>{box.name}</option>
                      ))}
                    </ClearableSelect>
                  </div>
                </div>
                {/* Search and breed filters */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('search', 'feedings')}</label>
                    <input
                      type="text"
                      placeholder={t('search_placeholder_animal', 'feedings')}
                      value={animalSearchTerm}
                      onChange={(e) => setAnimalSearchTerm(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('breed', 'feedings')}</label>
                    <ClearableSelect
                      value={animalBreedFilter}
                      onChange={(val) => setAnimalBreedFilter(val)}
                      placeholder={t('all_breeds', 'feedings')}
                    >
                      {uniqueBreeds.map(breed => (
                        <option key={breed} value={breed}>{breed}</option>
                      ))}
                    </ClearableSelect>
                  </div>
                </div>
                <div className="flex items-center justify-between text-sm text-gray-600">
                  <span>
                    {animalTotal > 0
                      ? `${((animalPage - 1) * animalPerPage) + 1}–${Math.min(animalPage * animalPerPage, animalTotal)} / ${animalTotal} ${t('animals', 'feedings')}`
                      : `0 ${t('animals', 'feedings')}`
                    }
                  </span>
                  {(animalSearchTerm || animalBreedFilter || stableFilter || boxFilter) && (
                    <button
                      onClick={() => {
                        setAnimalSearchTerm('');
                        setAnimalBreedFilter('');
                        handleStableFilterChange(null);
                      }}
                      className="text-green-600 hover:text-green-700"
                    >
                      {t('clear_filters', 'feedings')}
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="mb-4 space-y-3">
                {/* Hierarchical filter: Stable */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Istálló</label>
                    <ClearableSelect
                      value={stableFilter?.toString() || ''}
                      onChange={(val) => handleStableFilterChange(val ? parseInt(val) : null)}
                      placeholder="Összes istálló"
                    >
                      {stableOptions.map(stable => (
                        <option key={stable.id} value={stable.id}>{stable.name}</option>
                      ))}
                    </ClearableSelect>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('search', 'feedings')}</label>
                    <input
                      type="text"
                      placeholder={t('search_placeholder_box', 'feedings')}
                      value={boxSearchTerm}
                      onChange={(e) => setBoxSearchTerm(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('capacity', 'feedings')}</label>
                    <select
                      value={boxCapacityFilter}
                      onChange={(e) => setBoxCapacityFilter(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    >
                      <option value="">{t('all_sizes', 'feedings')}</option>
                      <option value="small">{t('small', 'feedings')} (≤10)</option>
                      <option value="medium">{t('medium', 'feedings')} (11-30)</option>
                      <option value="large">{t('large', 'feedings')} (&gt;30)</option>
                    </select>
                  </div>
                </div>
                <div className="flex items-center justify-between text-sm text-gray-600">
                  <span>{t('displayed', 'feedings')} {filteredBoxes.length} / {boxes.length} {t('boxes', 'feedings')}</span>
                  {(boxSearchTerm || boxCapacityFilter || stableFilter) && (
                    <button
                      onClick={() => {
                        setBoxSearchTerm('');
                        setBoxCapacityFilter('');
                        handleStableFilterChange(null);
                      }}
                      className="text-green-600 hover:text-green-700"
                    >
                      {t('clear_filters', 'feedings')}
                    </button>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'stable' ? (
              <div className="max-h-96 overflow-y-auto">
                {selectedStable ? (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-2">{t('stable_info', 'feedings')}</h4>
                    <p className="text-sm text-gray-600">
                      {t('feed_will_be_distributed', 'feedings')}
                    </p>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 48 48">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0v10a2 2 0 01-2 2H7a2 2 0 01-2-2V21m14 0H5m14 0h2" />
                    </svg>
                    <h3 className="mt-2 text-sm font-medium text-gray-900">{t('no_stable_selected', 'feedings')}</h3>
                    <p className="mt-1 text-sm text-gray-500">{t('select_stable_above', 'feedings')}</p>
                  </div>
                )}
              </div>
            ) : activeTab === 'individual' ? (
              <>
                <div className="relative grid grid-cols-1 md:grid-cols-2 gap-4 max-h-96 overflow-y-auto">
                  {animalsLoading && (
                    <div className="absolute inset-0 bg-white/70 flex items-center justify-center z-10">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
                    </div>
                  )}
                  {animals.length > 0 ? (
                    animals.map((animal) => (
                      <div
                        key={animal.id}
                        onClick={() => handleAnimalSelection(animal.id)}
                        className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                          selectedAnimals.includes(animal.id)
                            ? 'border-green-500 bg-green-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-medium text-gray-900">{animal.ear_tag}</h4>
                            <p className="text-sm text-gray-500">{animal.breed}</p>
                            <p className="text-sm text-gray-500">
                              Box: {(animal as any).current_box?.name || 'Nincs megadva'}
                            </p>
                          </div>
                          <div className={`w-5 h-5 rounded-full border-2 ${
                            selectedAnimals.includes(animal.id)
                              ? 'border-green-500 bg-green-500'
                              : 'border-gray-300'
                          }`}>
                            {selectedAnimals.includes(animal.id) && (
                              <svg className="w-3 h-3 text-white m-0.5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="col-span-2 text-center py-8">
                      <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 48 48">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M34 40h10v-4a6 6 0 00-10.712-3.714M34 40H14m20 0v-4a9.971 9.971 0 00-.712-3.714M14 40H4v-4a6 6 0 0110.713-3.714M14 40v-4c0-1.313.253-2.566.713-3.714m0 0A9.971 9.971 0 0124 24c4.21 0 7.863 2.613 9.287 6.286" />
                      </svg>
                      <h3 className="mt-2 text-sm font-medium text-gray-900">{t('no_results', 'feedings')}</h3>
                      <p className="mt-1 text-sm text-gray-500">{t('no_animal_found', 'feedings')}</p>
                    </div>
                  )}
                </div>
                {animalLastPage > 1 && (
                  <div className="mt-3 flex items-center justify-end">
                    <Pagination page={animalPage} lastPage={animalLastPage} onPageChange={setAnimalPage} />
                  </div>
                )}
              </>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-96 overflow-y-auto">
                {filteredBoxes.length > 0 ? (
                  filteredBoxes.map((box) => (
                    <div
                      key={box.id}
                      onClick={() => handleBoxSelection(box.id)}
                      className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                        selectedBoxes.includes(box.id)
                          ? 'border-green-500 bg-green-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium text-gray-900">{box.name}</h4>
                          <p className="text-sm text-gray-500">{t('capacity', 'feedings')}: {box.capacity}</p>
                          <p className="text-sm text-gray-500">
                            {t('animals', 'feedings')}: {(box as any).current_animal_count || 0}
                          </p>
                        </div>
                        <div className={`w-5 h-5 rounded-full border-2 ${
                          selectedBoxes.includes(box.id)
                            ? 'border-green-500 bg-green-500'
                            : 'border-gray-300'
                        }`}>
                          {selectedBoxes.includes(box.id) && (
                            <svg className="w-3 h-3 text-white m-0.5" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="col-span-2 text-center py-8">
                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 48 48">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5a2 2 0 00-2 2v8a2 2 0 002 2h14m15-5v2a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2h14" />
                    </svg>
                    <h3 className="mt-2 text-sm font-medium text-gray-900">{t('no_results', 'feedings')}</h3>
                    <p className="mt-1 text-sm text-gray-500">{t('no_box_found', 'feedings')}</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'individual' && (
              <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                <button
                  type="button"
                  onClick={() => setSelectionExpanded(!selectionExpanded)}
                  className="flex items-center justify-between w-full text-left"
                >
                  <span className="text-sm text-blue-700">
                    <strong>{t('selected_animals', 'feedings')}:</strong> {selectedAnimals.length}
                  </span>
                  <svg
                    className={`w-4 h-4 text-blue-500 transition-transform ${selectionExpanded ? 'rotate-180' : ''}`}
                    fill="none" stroke="currentColor" viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {selectionExpanded && selectedAnimals.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1 max-h-32 overflow-y-auto">
                    {Array.from(selectedAnimalMap.entries()).map(([id, earTag]) => (
                      <span
                        key={id}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                      >
                        {earTag}
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); handleAnimalSelection(id); }}
                          className="hover:text-blue-600"
                        >
                          &times;
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'group' && (
              <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-700">
                  <strong>{t('selected_boxes', 'feedings')}:</strong> {selectedBoxes.length}
                  {(boxSearchTerm || boxCapacityFilter) && 
                    ` (${filteredBoxes.filter(box => selectedBoxes.includes(box.id)).length} a szűrt listából)`
                  }
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Feeding Form Panel */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">{t('feeding_data', 'feedings')}</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('feed', 'feedings')} *
                </label>
                <select
                  value={feedingForm.feed_inventory_id}
                  onChange={(e) => {
                    const selectedFeed = availableFeeds.find(feed =>
                      feed.id.toString() === e.target.value
                    );
                    setFeedingForm({
                      ...feedingForm,
                      feed_inventory_id: e.target.value,
                      quantity_unit: selectedFeed ? selectedFeed.quantity_unit : ''
                    });
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  required
                >
                  <option value="">{t('select_feed', 'feedings')}</option>
                  {availableFeeds.map((feed) => (
                    <option key={feed.id} value={feed.id}>
                      {feed.display_name || feed.feed?.name} - {feed.quantity} {feed.quantity_unit} {t('stock', 'feedings')}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {activeTab === 'stable'
                    ? t('total_quantity_for_stable', 'feedings')
                    : activeTab === 'individual'
                      ? `${t('quantity')} ${t('quantity_per_animal', 'feedings')}`
                      : `${t('quantity')} ${t('quantity_per_box', 'feedings')}`
                  } *
                </label>
                {activeTab === 'stable' && (
                  <p className="text-xs text-gray-500 mb-1">
                    {t('total_quantity_for_stable_desc', 'feedings')}
                  </p>
                )}
                <input
                  type="number"
                  step="0.01"
                  value={feedingForm.quantity}
                  onChange={(e) => setFeedingForm({...feedingForm, quantity: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  required
                  min="0"
                  placeholder={activeTab === 'stable' ? t('eg_20_bales', 'feedings') : ''}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('unit', 'feedings')} *
                </label>
                <input
                  type="text"
                  value={feedingForm.quantity_unit}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 cursor-not-allowed"
                  disabled
                  placeholder={t('unit_auto_selected', 'feedings')}
                />
                <p className="text-xs text-gray-500 mt-1">
                  {t('unit_determined_by_feed', 'feedings')}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('feeding_date', 'feedings')} *
                </label>
                <input
                  type="date"
                  value={feedingForm.fed_at}
                  onChange={(e) => setFeedingForm({...feedingForm, fed_at: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('notes', 'feedings')}
                </label>
                <textarea
                  value={feedingForm.notes}
                  onChange={(e) => setFeedingForm({...feedingForm, notes: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  rows={3}
                />
              </div>

              {/* Summary */}
              {feedingForm.quantity && (
                <div className="p-3 bg-green-50 rounded-lg">
                  <h4 className="font-medium text-green-900 mb-2">{t('summary', 'feedings')}:</h4>
                  {activeTab === 'stable' ? (
                    <>
                      <p className="text-sm text-green-700">
                        <strong>{t('total_quantity_for_stable', 'feedings')}:</strong>{' '}
                        {parseFloat(feedingForm.quantity).toFixed(2)} {feedingForm.quantity_unit}
                      </p>
                      <p className="text-sm text-green-700">
                        <strong>{t('selected_stable', 'feedings')}:</strong>{' '}
                        {selectedStable ? stables.find(s => s.id === selectedStable)?.name : '-'}
                      </p>
                      <p className="text-xs text-green-600 mt-1">
                        {t('will_be_distributed_automatically', 'feedings')}
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="text-sm text-green-700">
                        <strong>{t('total_quantity', 'feedings')}:</strong>{' '}
                        {(parseFloat(feedingForm.quantity) *
                          (activeTab === 'individual' ? selectedAnimals.length : selectedBoxes.length)
                        ).toFixed(2)} {feedingForm.quantity_unit}
                      </p>
                      <p className="text-sm text-green-700">
                        <strong>{t('affected', 'feedings')} {activeTab === 'individual' ? t('affected_animals', 'feedings') : t('affected_boxes', 'feedings')}:</strong>{' '}
                        {activeTab === 'individual' ? selectedAnimals.length : selectedBoxes.length}
                      </p>
                    </>
                  )}
                </div>
              )}

              <button
                onClick={handleSubmit}
                disabled={
                  !feedingForm.feed_inventory_id ||
                  !feedingForm.quantity ||
                  (activeTab === 'stable'
                    ? !selectedStable
                    : activeTab === 'individual'
                      ? selectedAnimals.length === 0
                      : selectedBoxes.length === 0)
                }
                className="w-full bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {t('record_feeding', 'feedings')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Feeding;